import moment from 'moment';

/** The reasons the cancellation form offers, in the order it shows them. */
export const CANCELLATION_REASONS = ['no-use', 'unsatisfied', 'too-expensive', 'pet-deceased', 'pet-missing', 'other'] as const;
export type CancellationReason = typeof CANCELLATION_REASONS[number];

/**
 * When the contract ends. A pet that died or went missing has no type: that contract
 * ends on the date of death, or on the day of the report.
 */
export type CancellationType = 'end-of-insurance-year' | 'end-of-month';

/** The contract status the portal shows once CI marks a contract cancelled. */
export const CANCELLED_CONTRACT_STATUS = 'gekündigt';

export interface CancelInsuranceRequest {
  full_contract_number: string;
  selected_reason: CancellationReason;
  cancellation_type?: CancellationType;
  /** `YYYY-MM-DD`, for `end-of-month` only. */
  cancellation_date?: string;
  /** `YYYY-MM-DD`, for `pet-deceased` only. */
  pet_deceased_date?: string;
}

export interface CancellationFormValue {
  reason: CancellationReason;
  type: CancellationType;
  /** What the date picker holds: a Moment from the Moment adapter. */
  cancellationDate?: moment.MomentInput;
  petDeceasedDate?: moment.MomentInput;
}

export function isPetReason(reason: CancellationReason | ''): boolean {
  return reason === 'pet-deceased' || reason === 'pet-missing';
}

/**
 * The request for what the customer chose. Only the fields of the chosen reason go out:
 * the form keeps a date the customer picked and then left, and its type for a pet reason.
 * Dates go as the calendar day the customer picked, not as an instant, which would be
 * the day before in UTC.
 */
export function buildCancelInsuranceRequest(fullContractNumber: string, form: CancellationFormValue): CancelInsuranceRequest {
  const request: CancelInsuranceRequest = {
    full_contract_number: fullContractNumber,
    selected_reason: form.reason,
  };

  if (form.reason === 'pet-deceased') {
    request.pet_deceased_date = calendarDay(form.petDeceasedDate);
  } else if (!isPetReason(form.reason)) {
    request.cancellation_type = form.type;
    if (form.type === 'end-of-month') {
      request.cancellation_date = calendarDay(form.cancellationDate);
    }
  }
  return request;
}

function calendarDay(value: moment.MomentInput): string | undefined {
  return value ? moment(value).format('YYYY-MM-DD') : undefined;
}
